// Customer companies described as data: the Studio half of a client pack (the server half is
// VeraControlPlane.Host/clients/<key>.json, with the same role and workspace). Adding a client is one entry here plus
// that file; see docs/new-client-playbook.md in the control plane. Marine predates packs and stays in model.ts.
//
// A licensed copy of a pack (white label, subscription, licence sale) needs no entry: the role "<role>.<slug>" gets
// the same screens, and the server gives it its own company and data.
import type { AreaId, ScopeId } from "./model";
import type { Lang } from "../lib/i18n";

export type ClientPack = {
  key: string;
  /** The Entra app role, as in the server pack. */
  role: string;
  label: string;
  scope: ScopeId;
  /** The server pack's workspace. */
  workspaceId: string;
  /** Screen languages, the first one is where people start. */
  languages: Lang[];
  areas: AreaId[];
  /** Kinds of plug-ins in Connections (see lib/plugins.ts). */
  plugins: "security" | "consulting" | "marine" | "construction";
  /** What the "Tell VERA" bar suggests, in English (translated like every other text). */
  askHint: string;
  /** Currency of money fields (ISO code). */
  currency: string;
  /** The server has a demo firm for it: owners of an empty workspace can load it from Home. */
  demo?: boolean;
  /** Its clients get an owner portal: a private link made on the client's record. */
  portal?: boolean;
};

export const CLIENT_PACKS: ClientPack[] = [
  {
    key: "dama",
    role: "Studio.Client.DAMA",
    // Neutral on purpose: DAMA resells this to its own clients. Each company's own name comes from its profile.
    label: "Security",
    scope: "dama",
    workspaceId: "ws-dama",
    languages: ["de", "en"],
    areas: ["home", "workspace", "records", "field", "activity", "workflows", "files", "connections", "operations"],
    plugins: "security",
    askHint: 'Tell VERA, e.g. "Plan a night shift at the main entrance on Monday, 22:00 to 06:00"',
    currency: "CHF",
    demo: true,
  },
  // VERA AEC: architecture, engineering and construction, one pack per market. Neutral like DAMA: each company names
  // itself. Both run on the VERA Brain (11 layers, cost, schedule and quality) and come with a fictional demo firm.
  {
    key: "aecch",
    role: "Studio.Client.AECCH",
    label: "VERA AEC",
    scope: "aecch",
    workspaceId: "ws-aecch",
    languages: ["de", "en"],
    areas: ["home", "workspace", "records", "field", "activity", "workflows", "files", "connections", "operations"],
    plugins: "construction",
    askHint: 'Tell VERA, e.g. "Add a defect at Haus B, 2nd floor: spalled balcony edge, for the builder, fix within a week"',
    currency: "CHF",
    demo: true,
    portal: true,
  },
  {
    key: "aecus",
    role: "Studio.Client.AECUS",
    label: "VERA AEC",
    scope: "aecus",
    workspaceId: "ws-aecus",
    languages: ["en"],
    areas: ["home", "workspace", "records", "field", "activity", "workflows", "files", "connections", "operations"],
    plugins: "construction",
    askHint: 'Tell VERA, e.g. "Log an RFI for Riverside Lofts: duct penetration at level 2, to the structural engineer, answer by Friday"',
    currency: "USD",
    demo: true,
    portal: true,
  },
];

/** The pack a role belongs to: the pack's own role, or "<role>.<slug>" for a licensed copy. */
export const packOfRole = (role: string): ClientPack | undefined =>
  CLIENT_PACKS.find((p) => role === p.role || (role.startsWith(`${p.role}.`) && /^[a-z0-9]{2,30}$/.test(role.slice(p.role.length + 1))));
